import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Search, UserPlus, Phone, Mail, Eye, Users, Sparkles, Loader2, Tag,
} from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const TAGS = ["", "VIP", "Novo", "Recorrente"];

export default function CRM() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", tags: [] });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async (q = search, t = tag) => {
    const { data } = await api.get("/clients", { params: { search: q, tag: t } });
    setClients(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome do cliente."); return; }
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Cliente adicionado!");
      setOpen(false); setForm({ name: "", phone: "", email: "", tags: [] });
      load();
    } catch { toast.error("Erro ao adicionar cliente."); }
    finally { setSaving(false); }
  };

  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="crm-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 inline-flex items-center gap-2 text-xs font-mono-flow uppercase tracking-widest text-cyan-400"><Sparkles className="h-3.5 w-3.5" /> Relacionamento</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">CRM · Clientes</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="crm-add-customer-btn" className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300"><UserPlus className="mr-1.5 h-4 w-4" /> Adicionar cliente</Button>
          </DialogTrigger>
          <DialogContent className="border-slate-800 bg-slate-950 text-slate-100">
            <DialogHeader><DialogTitle className="font-display">Novo cliente</DialogTitle><DialogDescription className="text-slate-400">Cadastre o contato para a IA reconhecê-lo nas conversas.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-slate-300">Nome</Label><Input data-testid="crm-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="border-slate-700 bg-slate-900/60" /></div>
              <div className="space-y-1.5"><Label className="text-slate-300">Telefone</Label><Input data-testid="crm-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+55 11 99999-0000" className="border-slate-700 bg-slate-900/60" /></div>
              <div className="space-y-1.5"><Label className="text-slate-300">E-mail</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="border-slate-700 bg-slate-900/60" /></div>
              <div className="space-y-1.5"><Label className="text-slate-300">Tags</Label>
                <div className="flex gap-2">
                  {TAGS.filter(Boolean).map((t) => (
                    <button key={t} onClick={() => toggleTag(t)} className={`rounded-full border px-3 py-1 text-xs transition-all ${form.tags.includes(t) ? "border-cyan-400 bg-cyan-400/15 text-cyan-300" : "border-slate-700 text-slate-400"}`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} data-testid="crm-save-customer-btn" className="bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="crm-search-input" value={search} onChange={(e) => { setSearch(e.target.value); load(e.target.value, tag); }} placeholder="Buscar por nome, telefone ou e-mail..." className="h-11 border-slate-700 bg-slate-900/60 pl-10 text-slate-100" />
        </div>
        <div className="flex gap-2" data-testid="crm-status-filter">
          {TAGS.map((t) => (
            <button key={t || "all"} onClick={() => { setTag(t); load(search, t); }} className={`rounded-full border px-3.5 py-2 text-sm transition-all ${tag === t ? "border-cyan-400 bg-cyan-400/15 text-cyan-300" : "border-slate-700 bg-slate-900/40 text-slate-400 hover:text-slate-200"}`}>
              {t || "Todos"}
            </button>
          ))}
        </div>
      </div>

      <div className="glow-card overflow-hidden rounded-2xl">
        <div className="hidden grid-cols-[1fr_180px_120px_100px] gap-4 border-b border-slate-800 px-5 py-3 text-xs font-mono-flow uppercase tracking-wider text-slate-500 md:grid">
          <span>Cliente</span><span>Contato</span><span>Agendamentos</span><span className="text-right">Ações</span>
        </div>
        {clients.length === 0 && (
          <EmptyState icon={Users} testid="crm-empty"
            title={search || tag ? "Nenhum cliente encontrado" : "Seu CRM está vazio"}
            desc={search || tag ? "Ajuste a busca ou o filtro para ver outros clientes." : "Cadastre seu primeiro cliente ou deixe a IA criar automaticamente a partir das conversas no WhatsApp."}
            actionLabel={search || tag ? undefined : "Adicionar primeiro cliente"} onAction={() => setOpen(true)} />
        )}
        {clients.map((c) => (
          <div key={c.id} className="grid grid-cols-1 items-center gap-3 border-b border-slate-800/50 px-5 py-4 transition-colors hover:bg-slate-900/40 md:grid-cols-[1fr_180px_120px_100px]" data-testid="crm-customer-row">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-800 text-sm font-bold text-cyan-300">{initials(c.name)}</div>
              <div>
                <p className="font-semibold text-slate-100">{c.name}</p>
                <div className="mt-0.5 flex gap-1">{(c.tags || []).map((t) => <Badge key={t} className="border-cyan-400/30 bg-cyan-400/10 text-[10px] text-cyan-300"><Tag className="mr-0.5 h-2.5 w-2.5" />{t}</Badge>)}</div>
              </div>
            </div>
            <div className="text-sm text-slate-400">
              {c.phone && <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>}
              {c.email && <p className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {c.email}</p>}
            </div>
            <div className="text-sm text-slate-300"><span className="rounded-lg bg-slate-800/70 px-2 py-1">{c.appointments_count} agend.</span></div>
            <div className="flex justify-start md:justify-end">
              <Button size="sm" onClick={() => navigate(`/clientes/${c.id}`)} data-testid="crm-view-profile-button" className="bg-slate-800 text-cyan-300 hover:bg-slate-700"><Eye className="mr-1 h-4 w-4" /> Perfil</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
